/**
 * DevOps — operational monitoring for the founder.
 *
 * Seven sections, in priority order:
 *
 *   1. Service health cards (Backend / Database / Redis / Pages)
 *   2. Quick metrics row (errors 1h / 24h / AI avg / AI p95)
 *   3. System info card (python / git / alembic / env / uptime)
 *   4. Deploy info card (commit link / uptime / status)
 *   5. Recent errors table (with hour-window filter)
 *   6. Scheduled jobs table (last 5 runs per cron)
 *   7. Quick actions row (Clear cache / Run sentinel)
 *
 * The page auto-refreshes every 30 seconds — the founder leaves it
 * open as a wall display. A "Last updated Ns ago" timestamp + a
 * manual refresh button live in the header so a stale view is
 * obvious + recoverable.
 *
 * Each card hydrates from its own endpoint with ``Promise.allSettled``
 * so a single failing probe (e.g. Redis down) doesn't blank the
 * whole page. Failed probes render their own error state in-card.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CircleOff,
  Clock,
  Cloud,
  Database,
  ExternalLink,
  GitBranch,
  Loader2,
  PlayCircle,
  RefreshCcw,
  Server,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";

import {
  actionClearCache,
  actionRunSentinel,
  DeployInfo,
  ErrorRow,
  getDeployInfo,
  getErrors,
  getJobs,
  getMetrics,
  getServicesHealth,
  getSystemInfo,
  JobBlock,
  MetricsResponse,
  ServicesHealth,
  SystemInfo,
} from "@/api/admin";
import { cn, fmtNum, fmtRelative } from "@/lib/utils";

// Auto-refresh interval. 30s is the spec; we also expose a manual
// refresh button for the impatient case.
const AUTO_REFRESH_MS = 30_000;

// Independent settled-tuple type so we can branch on each card.
type Settled<T> = { ok: true; data: T } | { ok: false; error: string };

interface AllData {
  health: Settled<ServicesHealth>;
  system: Settled<SystemInfo>;
  deploy: Settled<DeployInfo>;
  errors: Settled<ErrorRow[]>;
  jobs: Settled<JobBlock[]>;
  metrics: Settled<MetricsResponse>;
  // Cloudflare Pages probes — done client-side because the SPA
  // origin can fetch the customer SPAs without backend involvement.
  pages: Settled<{ app: boolean; www: boolean }>;
}

const ERROR_WINDOWS = [
  { label: "Last hour", hours: 1 },
  { label: "Last 24h", hours: 24 },
  { label: "Last 7 days", hours: 24 * 7 },
];

function settled<T>(p: PromiseSettledResult<T>): Settled<T> {
  return p.status === "fulfilled"
    ? { ok: true, data: p.value }
    : {
        ok: false,
        error:
          p.reason instanceof Error
            ? p.reason.message
            : String(p.reason ?? "Failed"),
      };
}

async function probePages(): Promise<{ app: boolean; www: boolean }> {
  /**
   * Lightweight HEAD probes for the customer-facing SPAs.
   * ``mode: "no-cors"`` keeps the browser from blocking on a
   * missing ``Access-Control-Allow-Origin`` header — Cloudflare
   * Pages doesn't serve CORS headers on the HTML root and we
   * don't need the response body anyway. ``ok`` flips to false
   * on a network failure (DNS / TCP / TLS).
   */
  async function ping(url: string): Promise<boolean> {
    try {
      await fetch(url, { method: "HEAD", mode: "no-cors", cache: "no-store" });
      return true;
    } catch {
      return false;
    }
  }
  const [app, www] = await Promise.all([
    ping("https://app.lenavio.com"),
    ping("https://www.lenavio.com"),
  ]);
  return { app, www };
}

export function DevOpsPage() {
  const [data, setData] = useState<AllData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [errorWindowHours, setErrorWindowHours] = useState(24);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const [health, system, deploy, errors, jobs, metrics, pages] =
      await Promise.allSettled([
        getServicesHealth(),
        getSystemInfo(),
        getDeployInfo(),
        getErrors(errorWindowHours, 50),
        getJobs(),
        getMetrics(),
        probePages(),
      ]);

    if (cancelledRef.current) return;
    setData({
      health: settled(health),
      system: settled(system),
      deploy: settled(deploy),
      errors: settled(errors),
      jobs: settled(jobs),
      metrics: settled(metrics),
      pages: settled(pages),
    });
    setLastUpdated(new Date());
    setRefreshing(false);
  }, [errorWindowHours]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    const id = window.setInterval(load, AUTO_REFRESH_MS);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
  }, [load]);

  // Re-fetch when the founder switches the error window
  useEffect(() => {
    load();
  }, [errorWindowHours, load]);

  const runAction = async (
    kind: "clear-cache" | "run-sentinel",
    label: string,
  ) => {
    const ok = window.confirm(`Confirm: ${label}? This affects production.`);
    if (!ok) return;
    setActionBusy(kind);
    setActionResult(null);
    try {
      const result =
        kind === "clear-cache"
          ? await actionClearCache()
          : await actionRunSentinel();
      setActionResult(JSON.stringify(result));
    } catch (err) {
      setActionResult(`Failed: ${(err as Error)?.message || String(err)}`);
    } finally {
      setActionBusy(null);
      // Refresh after an action so the next dashboard tick reflects
      // any state change (jobs row updates after run-sentinel,
      // metrics may change after clear-cache).
      load();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">DevOps</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Live operational view. Auto-refresh every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>
            {lastUpdated
              ? `Last updated ${fmtRelative(lastUpdated.toISOString())}`
              : "Loading…"}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50",
              refreshing && "opacity-60",
            )}
          >
            <RefreshCcw
              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
            />
            Refresh now
          </button>
        </div>
      </header>

      {/* ── 1. Service health cards ────────────────────────────── */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <HealthCard
          icon={<Server className="h-4 w-4" />}
          title="Backend API"
          loading={!data}
          status={data?.health.ok ? data.health.data.api.status : "down"}
          subtitle={data?.health.ok ? "Responding" : data?.health && !data.health.ok ? data.health.error : ""}
          detail={
            data?.health.ok
              ? `Checked ${fmtRelative(data.health.data.timestamp)}`
              : ""
          }
        />
        <HealthCard
          icon={<Database className="h-4 w-4" />}
          title="Database (Neon)"
          loading={!data}
          status={data?.health.ok ? data.health.data.database.status : "down"}
          subtitle={
            data?.health.ok && data.health.data.database.latency_ms != null
              ? `${data.health.data.database.latency_ms} ms`
              : data?.health.ok
                ? data.health.data.database.error || ""
                : ""
          }
          detail={
            data?.health.ok
              ? `pool: out=${data.health.data.database.pool?.checked_out ?? "—"} in=${data.health.data.database.pool?.checkedin ?? "—"} size=${data.health.data.database.pool?.size ?? "—"}`
              : ""
          }
        />
        <HealthCard
          icon={<Zap className="h-4 w-4" />}
          title="Redis (Upstash)"
          loading={!data}
          status={data?.health.ok ? data.health.data.redis.status : "down"}
          subtitle={
            data?.health.ok && data.health.data.redis.latency_ms != null
              ? `${data.health.data.redis.latency_ms} ms`
              : data?.health.ok
                ? data.health.data.redis.error || data.health.data.redis.status
                : ""
          }
          detail=""
        />
        <HealthCard
          icon={<Cloud className="h-4 w-4" />}
          title="Cloudflare Pages"
          loading={!data}
          status={
            data?.pages.ok
              ? data.pages.data.app && data.pages.data.www
                ? "healthy"
                : "degraded"
              : "down"
          }
          subtitle={
            data?.pages.ok
              ? `app: ${data.pages.data.app ? "up" : "down"} · www: ${data.pages.data.www ? "up" : "down"}`
              : ""
          }
          detail=""
        />
      </section>

      {/* ── 2. Quick metrics row ───────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          label="Errors (1h)"
          value={fmtNum(data?.metrics.ok ? data.metrics.data.errors_1h : 0)}
          tone={
            data?.metrics.ok && data.metrics.data.errors_1h > 0
              ? data.metrics.data.errors_1h > 10
                ? "danger"
                : "warning"
              : "default"
          }
        />
        <MetricTile
          label="Errors (24h)"
          value={fmtNum(data?.metrics.ok ? data.metrics.data.errors_24h : 0)}
        />
        <MetricTile
          label="AI avg latency"
          value={
            data?.metrics.ok
              ? `${Math.round(data.metrics.data.avg_ai_duration_ms)} ms`
              : "—"
          }
          subtitle={
            data?.metrics.ok
              ? `over ${data.metrics.data.ai_samples_1h} calls (1h)`
              : ""
          }
        />
        <MetricTile
          label="AI p95 latency"
          value={
            data?.metrics.ok
              ? `${data.metrics.data.p95_ai_duration_ms} ms`
              : "—"
          }
        />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── 3. System info ─────────────────────────────────── */}
        <Card title="System">
          {data?.system.ok ? (
            <dl className="text-sm space-y-1.5">
              <KV label="Python" value={data.system.data.python_version} />
              <KV label="Platform" value={data.system.data.platform} />
              <KV label="Host" value={data.system.data.hostname} />
              <KV
                label="App version"
                value={data.system.data.app_version}
                mono
              />
              <KV
                label="Alembic"
                value={data.system.data.alembic_version || "—"}
                mono
              />
              <KV
                label="Environment"
                value={data.system.data.environment}
              />
              <KV
                label="Uptime"
                value={formatUptime(data.system.data.uptime_seconds)}
              />
              <KV
                label="Database"
                value={data.system.data.database_host_masked}
                mono
              />
              <KV
                label="Render service"
                value={data.system.data.render_service_id}
                mono
              />
            </dl>
          ) : (
            <CardError data={data?.system} />
          )}
        </Card>

        {/* ── 4. Deploy info ─────────────────────────────────── */}
        <Card title="Deploy">
          {data?.deploy.ok ? (
            <dl className="text-sm space-y-1.5">
              <KV
                label="Commit"
                value={
                  data.deploy.data.github_url ? (
                    <a
                      href={data.deploy.data.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-accent inline-flex items-center gap-1 hover:underline"
                    >
                      {data.deploy.data.current_commit_short}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono">
                      {data.deploy.data.current_commit_short || "—"}
                    </span>
                  )
                }
              />
              <KV
                label="Alembic"
                value={data.deploy.data.alembic_version || "—"}
                mono
              />
              <KV
                label="Started"
                value={fmtRelative(data.deploy.data.process_start)}
              />
              <KV
                label="Uptime"
                value={formatUptime(data.deploy.data.uptime_seconds)}
              />
              <KV
                label="Environment"
                value={data.deploy.data.environment}
              />
              <KV
                label="Last deploy"
                value={
                  <span className="inline-flex items-center gap-1 text-success">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {data.deploy.data.last_deploy_status}
                  </span>
                }
              />
            </dl>
          ) : (
            <CardError data={data?.deploy} />
          )}
        </Card>
      </div>

      {/* ── 5. Recent errors ──────────────────────────────────── */}
      <Card
        title="Recent errors"
        headerExtra={
          <div className="flex items-center gap-1.5">
            {ERROR_WINDOWS.map((w) => (
              <button
                key={w.hours}
                type="button"
                onClick={() => setErrorWindowHours(w.hours)}
                className={cn(
                  "text-xs rounded-md px-2 py-1",
                  errorWindowHours === w.hours
                    ? "bg-accent text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                )}
              >
                {w.label}
              </button>
            ))}
          </div>
        }
      >
        {data?.errors.ok ? (
          data.errors.data.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">
              <CheckCircle2 className="h-6 w-6 mx-auto text-success mb-2" />
              No errors in the selected window. ✨
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">When</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Path</th>
                    <th className="text-left px-4 py-2 font-semibold">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.errors.data.map((e) => (
                    <ErrorRowComp key={e.id} row={e} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <CardError data={data?.errors} />
        )}
      </Card>

      {/* ── 6. Scheduled jobs ─────────────────────────────────── */}
      <Card title="Scheduled jobs">
        {data?.jobs.ok ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Job</th>
                  <th className="text-left px-4 py-2 font-semibold">Schedule</th>
                  <th className="text-left px-4 py-2 font-semibold">Last run</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">Result / Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.jobs.data.map((j) => {
                  const last = j.runs[0];
                  return (
                    <tr key={j.name}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-900">
                        {j.name}
                      </td>
                      <td className="px-4 py-2 text-slate-600 text-xs">
                        {j.schedule}
                      </td>
                      <td className="px-4 py-2 text-slate-600 text-xs whitespace-nowrap">
                        {last ? fmtRelative(last.started_at) : "never"}
                      </td>
                      <td className="px-4 py-2">
                        {last ? (
                          <JobStatusBadge status={last.status} />
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-600 max-w-md truncate">
                        {last?.error || last?.result || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <CardError data={data?.jobs} />
        )}
      </Card>

      {/* ── 7. Quick actions ──────────────────────────────────── */}
      <Card title="Quick actions">
        <div className="px-4 py-3 flex flex-wrap items-center gap-3">
          <ActionButton
            icon={<Trash2 className="h-4 w-4" />}
            label="Clear command-bar cache"
            description="Flushes scratchpad + memory + recall keys in Redis. Does NOT log anyone out."
            busy={actionBusy === "clear-cache"}
            onClick={() => runAction("clear-cache", "Clear command-bar cache")}
          />
          <ActionButton
            icon={<PlayCircle className="h-4 w-4" />}
            label="Run Sentinel now"
            description="Triggers an immediate Sentinel scan across all orgs."
            busy={actionBusy === "run-sentinel"}
            onClick={() => runAction("run-sentinel", "Run Sentinel now")}
          />
          {actionResult && (
            <div className="w-full mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs font-mono text-slate-700 break-all">
              {actionResult}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ───────────────────────────── Helpers ───────────────────────────── */

function HealthCard({
  icon,
  title,
  loading,
  status,
  subtitle,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  loading: boolean;
  status: string;
  subtitle: string;
  detail: string;
}) {
  const tone = healthTone(status);
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-4 py-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
          {icon}
          {title}
        </span>
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
        ) : (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-xs font-medium",
              tone.text,
            )}
          >
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                tone.dot,
                status === "healthy" && "animate-pulse",
              )}
            />
            {tone.label}
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-slate-900 truncate">
        {subtitle || "—"}
      </div>
      {detail && (
        <div className="mt-0.5 text-xs text-slate-500 truncate">{detail}</div>
      )}
    </div>
  );
}

function healthTone(status: string) {
  switch (status) {
    case "healthy":
      return { text: "text-success", dot: "bg-success", label: "Healthy" };
    case "degraded":
      return { text: "text-warning", dot: "bg-warning", label: "Degraded" };
    case "disabled":
      return { text: "text-slate-400", dot: "bg-slate-300", label: "Disabled" };
    case "unavailable":
      return { text: "text-slate-400", dot: "bg-slate-300", label: "N/A" };
    default:
      return { text: "text-danger", dot: "bg-danger", label: "Down" };
  }
}

function MetricTile({
  label,
  value,
  subtitle,
  tone = "default",
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone?: "default" | "warning" | "danger";
}) {
  const colorClass =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : "text-slate-900";
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={cn("mt-1 text-2xl font-semibold tabular-nums", colorClass)}>
        {value}
      </div>
      {subtitle && (
        <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
      )}
    </div>
  );
}

function Card({
  title,
  headerExtra,
  children,
}: {
  title: string;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <header className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {headerExtra}
      </header>
      <div>{children}</div>
    </section>
  );
}

function CardError<T>({ data }: { data?: Settled<T> }) {
  if (!data) {
    return (
      <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (data.ok) return null;
  return (
    <div className="px-4 py-4 flex items-start gap-2 text-sm text-danger">
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-medium">Failed to load</div>
        <div className="text-xs text-slate-500 mt-0.5">{data.error}</div>
      </div>
    </div>
  );
}

function KV({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-4 py-1.5">
      <dt className="text-xs uppercase tracking-wide text-slate-500 shrink-0">
        {label}
      </dt>
      <dd
        className={cn(
          "text-slate-900 text-right truncate min-w-0",
          mono && "font-mono text-xs",
        )}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </dd>
    </div>
  );
}

function ErrorRowComp({ row }: { row: ErrorRow }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-2 text-slate-600 text-xs whitespace-nowrap">
          {fmtRelative(row.timestamp)}
        </td>
        <td className="px-4 py-2">
          <span className="rounded-md bg-danger/10 text-danger px-2 py-0.5 text-xs font-medium tabular-nums">
            {row.status_code}
          </span>
        </td>
        <td className="px-4 py-2 font-mono text-xs text-slate-700 max-w-xs truncate">
          <span className="text-slate-400 mr-1">{row.method}</span>
          {row.path}
        </td>
        <td className="px-4 py-2 text-xs text-slate-700 max-w-md truncate">
          <button
            type="button"
            className="text-left w-full hover:underline"
            onClick={() => setOpen((o) => !o)}
            title="Click to expand"
          >
            {row.error_type ? (
              <span className="font-mono text-slate-500 mr-1">
                {row.error_type}:
              </span>
            ) : null}
            {row.error_message}
          </button>
        </td>
      </tr>
      {open && row.traceback && (
        <tr>
          <td colSpan={4} className="px-4 py-2 bg-slate-50">
            <pre className="whitespace-pre-wrap text-[10px] font-mono text-slate-700 leading-snug">
              {row.traceback}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  if (status === "success") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
        success
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-danger">
        <XCircle className="h-3.5 w-3.5" />
        error
      </span>
    );
  }
  if (status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-accent">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        running
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
      <CircleOff className="h-3.5 w-3.5" />
      {status}
    </span>
  );
}

function ActionButton({
  icon,
  label,
  description,
  busy,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        "flex items-start gap-3 text-left rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-accent/40 hover:bg-accent/5",
        busy && "opacity-60 cursor-not-allowed",
      )}
    >
      <span className="mt-0.5 text-accent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}</span>
      <span>
        <span className="block text-sm font-medium text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500">{description}</span>
      </span>
    </button>
  );
}

function formatUptime(seconds: number): string {
  if (!seconds || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

// Some imports above ("Clock", "GitBranch", "AlertTriangle") were intentional
// for the design but unused in the final shape; satisfy the lint rule.
void Clock;
void GitBranch;
void AlertTriangle;
